/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Flex, Text, Link, AddToCalendarButton } from '@tishman/components';
import * as Icons from '@tishman/icons';
import { useLocation } from '@reach/router';
import { addHours, formatISO } from 'date-fns';
import React, { useMemo } from 'react';

import { selectShopCartDateTime, useAppSelector } from '../../../store';
import { Flow } from '../../../services/viva/constants';
import { ROUTES } from '../../../constants';

const subject = encodeURIComponent("Let's Take a Trip to the Top");
const body = encodeURIComponent(
  'Hi Friend -\n\nA visit to New York isn’t complete without a trip to Top of the Rock and Rockefeller Center. Spend the day exploring with me – buy your ticket online!\n\nwww.topoftherocknyc.com | www.rockefellercenter.com',
);

export const ShareTrip = (): React.JSX.Element => {
  const location = useLocation();
  const order = useAppSelector((state) => state.order);

  const performanceDateTime = useAppSelector(selectShopCartDateTime);

  const url = useMemo(() => {
    const baseUrl = `${location.origin}/buy-tickets`;

    switch (order.flow) {
      case Flow.DECK:
        return `${baseUrl}/top-of-the-rock`;
      case Flow.RC_TOUR:
        return `${baseUrl}/rock-center-tour`;
      case Flow.TREE_PHOTO:
        return `${baseUrl}/tree-photo`;
      case Flow.RC_TOUR_KIDS_AND_FAMILY:
        return `${baseUrl}/${ROUTES.RC_TOUR_KIDS_AND_FAMILY}`;
      case Flow.TOR_VIP:
        return `${baseUrl}/vip`;
      case Flow.TOR_VIP_ROCKSTAR:
        return `${baseUrl}/${ROUTES.VIP_ROCKSTAR}`;
      case Flow.RC_ROCK_PASS_VIP_HOLIDAY:
        return `${baseUrl}/${ROUTES.RC_ROCK_PASS_VIP_HOLIDAY}`;
      case Flow.RC_ROCK_PASS:
        return `${baseUrl}/rock-pass`;
      case Flow.CITY_PASS:
        return `${baseUrl}/citypass`;
      case Flow.C3:
        return `${baseUrl}/c3`;
      case Flow.RINK:
        return `${baseUrl}/the-rink`;
      case Flow.CHALETS:
        return `${baseUrl}/${ROUTES.CHALETS}`;
      case Flow.RINK_VIP:
        return `${baseUrl}/${ROUTES.RINK_VIP}`;
      default:
        return baseUrl;
    }
  }, [order.flow, location.origin]);

  const { endTime, startTime } = useMemo(
    () =>
      order
        ? {
            endTime: formatISO(
              addHours(order.shopCart.dateTime ?? performanceDateTime, 1),
            ),
            startTime: formatISO(
              order.shopCart.dateTime ?? performanceDateTime,
            ),
          }
        : {
            endTime: '',
            startTime: '',
          },
    [order, performanceDateTime],
  );

  return (
    <Flex
      sx={{
        mt: [2, 0],
        width: 152,
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Text sx={{ letterSpacing: 2, pt: 2 }}>Share Trip</Text>
      <Flex sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Link to={`mailto:?subject=${subject}&body=${body}`}>
          <Icons.MailIcon />
        </Link>
        <AddToCalendarButton
          description={url}
          endDateTime={endTime}
          icon
          location="Rockefeller Center, 30 Rockefeller Plaza, New York, NY 10112"
          startDateTime={startTime}
          title="Visit to Top of the Rock"
          url={url}
        />
        <Link to="https://www.facebook.com/sharer.php?u=https://www.rockefellercenter.com/buy-tickets/">
          <Icons.FbSm height={24} width={24} />
        </Link>
        <Link to="https://x.com/intent/tweet?url=https%3A%2F%2Fwww.rockefellercenter.com%2Fbuy-tickets%2F&text=I%E2%80%99m%20going%20to%20see%20the%20view%20from%20%23TopOfTheRock%20at%20Rockefeller%20Center.%20">
          <Icons.XSm height={24} width={24} />
        </Link>
      </Flex>
    </Flex>
  );
};
